import SectionStatusType from '../types/SectionStatusType';

export const X_DEFAULT = 'x-default';
class RouteService {
  constructor(categoryStore, configStore, languageStore, sectionStore) {
    this.categoryStore = categoryStore;
    this.configStore = configStore;
    this.languageStore = languageStore;
    this.sectionStore = sectionStore;
  }

  findCanonicalSectionForCategory(category) {
    if (category) {
      // Return the first public section that includes this category
      return this.sectionStore.sections.find(
        (section) =>
          section.includesCategory(category) &&
          section.status === SectionStatusType.ACTIVE
      );
    }
    return null;
  }

  findCanonicalSection(sectionIds) {
    // Get the first section ID that has a matching section
    // In a multi-domain store there might be section IDs that do not match.
    const defaultSectionId = sectionIds.find((sectionId) =>
      this.sectionStore.findSectionById(sectionId)
    );
    return (
      defaultSectionId && this.sectionStore.findSectionById(defaultSectionId)
    );
  }

  transformBreadCrumbs(breadcrumbs) {
    let transformedBreadcrumbs = [];

    if (this.sectionStore.activeSection) {
      transformedBreadcrumbs.push({
        text: this.sectionStore.activeSection.display_name,
        url: this.getPath(this.sectionStore.activeSection.path),
      });
    }

    return transformedBreadcrumbs.concat(
      breadcrumbs.map((breadcrumb) => {
        const url =
          breadcrumb.url &&
          this.getPath(breadcrumb.url, this.sectionStore.activeSection);
        return {
          ...breadcrumb,
          url,
        };
      })
    );
  }

  getCategoryPath(category, path) {
    let section;
    if (
      this.sectionStore.activeSection &&
      this.sectionStore.activeSection.includesCategory(category)
    ) {
      section = this.sectionStore.activeSection;
    } else {
      section = this.findCanonicalSectionForCategory(category);
    }
    return this.getPath(path || category.path, section);
  }

  getCanonicalCategoryPath(category, path, locale) {
    let section = this.findCanonicalSectionForCategory(category);

    return this.getPath(path || category.path, section, locale);
  }

  /**
   *
   * @param {Product|OrderProduct} product required
   * @param {string} path override path, defaults products path
   * @param {string} locale override locale, defaults to active locale
   * @returns {string} canonical product path
   */
  getCanonicalProductPath(product, path, locale) {
    // Prioritize main_section_id as canonical section
    let canonicalSection = this.configStore.activateSections
      ? this.findCanonicalSection([product.main_section_id])
      : null;
    const hasCategory = !!product.canonicalCategoryId;

    if (!canonicalSection && hasCategory) {
      const category = this.categoryStore.findCategoryById(
        product.canonicalCategoryId
      );
      canonicalSection = this.findCanonicalSectionForCategory(category);
    }

    return this.getPath(path || product.path, canonicalSection, locale);
  }

  getProductPath(product, path) {
    let section;
    if (
      this.sectionStore.activeSection &&
      product.belongsToSection(this.sectionStore.activeSection.id)
    ) {
      section = this.sectionStore.activeSection;
    } else {
      section = this.findCanonicalSection(product.section_ids);
    }
    return this.getPath(path || product.path, section);
  }

  /**
   *
   * @param {String} path
   * @param {Section} section Used when forming whole path with section and sub-categories
   * @param {String} locale
   * @returns {String}
   */
  getPath(path, section, locale) {
    const { languages } = this.languageStore;
    const { defaultLocale, activateSections } = this.configStore;
    const currentLocale = locale === X_DEFAULT ? defaultLocale : locale;
    const routeLocale = currentLocale || this.languageStore.activeLocale;
    const languagePrefix = languages.length > 1 ? `/${routeLocale}` : '';

    let sectionPrefix = '';
    if (activateSections && section) {
      let slug = section.slug;
      if (section.localized_slugs?.[currentLocale]) {
        slug = section.localized_slugs[currentLocale];
      }
      sectionPrefix = `/${slug}`;
    }

    return languagePrefix + sectionPrefix + path;
  }
}

export default RouteService;
